import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fade } from '@/components/ui/fade';
import { cn } from '@/utils/cn';
import { useApproachers } from '../hooks/use-approachers';
import { useSelectedMember } from '@/hooks/use-selected-member';
import { ApproacherList } from './approacher-list';
import { ApproacherDetailModal } from './approacher-detail-modal';
import { ApproachersToolbar } from './approachers-toolbar';
import { useMinLoading } from '@/hooks/use-min-loading';

type View = 'active' | 'archived';

function useFiltered(search: string, view: View) {
  const q = useApproachers({ includeDeleted: view === 'archived' });
  const lc = search.toLowerCase();
  const data = q.data?.filter(
    (a) =>
      a.ig_username.toLowerCase().includes(lc) ||
      a.email.toLowerCase().includes(lc)
  );
  return { ...q, data };
}

function usePageState() {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('active');
  const { selectedId, setSelectedId } = useSelectedMember();
  return { search, setSearch, selectedId, setSelectedId, view, setView };
}

const TAB =
  'rounded-md px-md py-1 text-xs font-medium transition-colors focus:outline-none';
const TAB_ACTIVE = 'bg-surface-elevated text-text-primary';
const TAB_INACTIVE = 'text-text-tertiary hover:text-text-secondary';

function ViewTabs({
  view,
  onChange,
}: {
  view: View;
  onChange: (v: View) => void;
}): ReactNode {
  return (
    <div className="flex items-center gap-xs">
      <button
        className={cn(TAB, view === 'active' ? TAB_ACTIVE : TAB_INACTIVE)}
        onClick={() => onChange('active')}
      >
        Activos
      </button>
      <button
        className={cn(TAB, view === 'archived' ? TAB_ACTIVE : TAB_INACTIVE)}
        onClick={() => onChange('archived')}
      >
        Archivados
      </button>
    </div>
  );
}

type PageContentProps = ReturnType<typeof usePageState> & {
  onAdd: () => void;
};

function PageContent(s: PageContentProps): ReactNode {
  const { data, isLoading } = useFiltered(s.search, s.view);
  const skeleton = useMinLoading(isLoading);
  return (
    <>
      <ApproachersToolbar
        search={s.search}
        onSearch={s.setSearch}
        onAdd={s.onAdd}
      />
      <ViewTabs view={s.view} onChange={s.setView} />
      <Fade activeKey={isLoading || skeleton ? 'skeleton' : 'data'}>
        <ApproacherList
          data={data}
          showSkeleton={isLoading || skeleton}
          onSelect={s.setSelectedId}
        />
      </Fade>
    </>
  );
}

export function ApproacherSPage(): ReactNode {
  const s = usePageState();
  const navigate = useNavigate();
  return (
    <div className="space-y-lg">
      <PageContent {...s} onAdd={() => navigate('/admin/team/new')} />
      <ApproacherDetailModal
        approacherId={s.selectedId}
        onClose={() => s.setSelectedId(null)}
      />
    </div>
  );
}
